import React from 'react';
import {
  match,
  matchPath,
  useHistory,
  useLocation,
  useParams,
  useRouteMatch,
} from 'react-router-dom';

import { LocationDescriptor } from 'history';

import { GlobalMessages } from '../../types/Messages';
import { languageUtils } from '../../utils/languageUtils/languageUtils';
import { serverUtils } from '../../utils/serverUtils/serverUtils';
import { messageHooks } from '../messageHooks/messageHooks';

export type QueryObject = {
  [key: string]: string;
};

export type Location = LocationDescriptor & {
  hash?: string;
  pathname?: string;
};
type ReplaceFunction = (location: Location) => void;

export type RouterHooks = {
  useGetQueryString(): string;
  useGoBack(): (() => void) | null;
  useHash(): string;
  useListen(): (callback: () => void) => void;
  useMatch(path: string, pathTo?: string): match<Record<string, string>> | null;
  usePageParams<T,>(withPageId?: boolean): T | null;
  useParams(): Record<string, string> | null;
  usePath(): string | null;
  usePathName(): string;
  usePushQueryHandler(): (params: {
    deleteParams: string[];
    setParams: QueryObject;
  }) => void;
  useQueryParams(): Record<string, string | undefined>;
  useReplace(): ReplaceFunction;
  useSearch(): string;
  useSlugPathname(): string;
};

export const nextRouterHooks: RouterHooks = {
  useGetQueryString() {
    const router = require('next/router').useRouter();
    const position = String(router.asPath).indexOf('?');
    return position !== -1 ? String(router.asPath).slice(position) : '';
  },
  useQueryParams() {
    const router = require('next/router').useRouter();
    return router.query as Record<string, string | undefined>;
  },
  useSearch() {
    const router = require('next/router').useRouter();
    const search = router.asPath.includes('?') ? router.asPath.split('?')[1] : '';

    return search ? '?' + search : '';
  },
  usePathName() {
    const router = require('next/router').useRouter();

    return router.asPath;
  },
  useHash() {
    const router = require('next/router').useRouter();
    const { asPath } = router;
    const hashIndex = asPath.indexOf('#');

    if (hashIndex === -1) {
      return '';
    }

    const hash = asPath.slice(hashIndex + 1);
    return hash ? '#' + hash : '';
  },
  usePushQueryHandler() {
    const router = require('next/router').useRouter();
    return React.useCallback(({ setParams: query }) => {
      router.push({ query });
    }, [router]);
  },
  useListen() {
    return React.useCallback((callback) => callback(), []);
  },
  useMatch() {
    return null;
  },
  useReplace() {
    const router = require('next/router').useRouter();

    const replace: ReplaceFunction = ({ pathname, search, hash }) => {
      const as = `${pathname}${search ?? ''}${hash ?? ''}`;
      return router.replace(pathname, as);
    };

    return replace;
  },
  useParams() {
    return null;
  },
  usePath() {
    return null;
  },
  usePageParams<T,>(withPageId: boolean): T {
    const router = require('next/router').useRouter();
    if (!withPageId) {
      return router.query as T;
    } else {
      const pathArray = router.query.slug;
      const isPageParam = !isNaN(Number(pathArray[pathArray.length - 1]));
      return isPageParam
        ? {
          page: pathArray[pathArray.length - 1],
          pageId: pathArray[pathArray.length - 2],
        } as unknown as T : {
          page: undefined,
          pageId: pathArray[pathArray.length - 1],
        } as unknown as T;
    }
  },
  useGoBack() {
    return null;
  },
  useSlugPathname() {
    const router = require('next/router').useRouter();
    const lang = messageHooks.useSubjectState(GlobalMessages.Language);
    const isDefaultLanguage = !lang || lang.slice(0, 2) === languageUtils.getDefaultLanguage();
    const path = serverUtils.buildPathFromSlug(router.query?.slug);
    return isDefaultLanguage ? path : `/${lang}${path}`;
  },
};

export const reactRouterHooks: RouterHooks = {
  useGetQueryString() {
    const { search } = useLocation();
    return search;
  },
  useQueryParams() {
    const location = useLocation();
    const queryParams = React.useMemo(() => new URLSearchParams(location.search), [
      location.search,
    ]);
    return React.useMemo(
      () =>
        [...queryParams.entries()].reduce((queryObject, [param, value]) => {
          return {
            ...queryObject,
            [param]: value,
          };
        }, {}),
      [queryParams],
    );
  },
  useSearch() {
    const location = useLocation();
    return location.search;
  },
  usePathName() {
    const location = useLocation();
    return location.pathname;
  },
  useSlugPathname() {
    const location = useLocation();
    return location.pathname;
  },
  useHash() {
    const location = useLocation();
    return location.hash;
  },
  usePushQueryHandler() {
    const history = useHistory();
    const { search } = useLocation();
    const queryParams = React.useMemo(() => new URLSearchParams(search), [search]);
    const setQueryParamsHandler = React.useCallback(
      (setParams: QueryObject) => {
        Object.keys(setParams).forEach(param => {
          queryParams.set(param, setParams[param]);
        });
        return queryParams;
      },
      [queryParams],
    );
    const deleteQueryParamsHandler = React.useCallback(
      (deleteParams: string[]) => {
        deleteParams.forEach(param => {
          queryParams.delete(param);
        });
        return queryParams;
      },
      [queryParams],
    );
    return React.useCallback(
      ({ setParams, deleteParams }) => {
        deleteQueryParamsHandler(deleteParams);
        const params = setQueryParamsHandler(setParams);
        history.push({ search: params.toString() });
      },
      [deleteQueryParamsHandler, history, setQueryParamsHandler],
    );
  },
  useListen() {
    const { listen } = useHistory();
    return listen;
  },
  useMatch(path, pathTo) {
    const { pathname } = useLocation();
    return matchPath(pathTo || pathname, {
      path: path,
      exact: true,
    });
  },
  useReplace() {
    const { replace } = useHistory();
    return replace;
  },
  useParams() {
    return useParams<Record<string, string>>();
  },
  usePath() {
    return useRouteMatch().path;
  },
  usePageParams<T,>(withPageId: boolean) {
    const { pathname } = useLocation();
    const pathArray = pathname.split('/').filter(param => param != '');
    const isPageParam = !isNaN(Number(pathArray[pathArray.length - 1]));

    if (!withPageId) {
      return isPageParam ? { page: pathArray[pathArray.length - 1] } as unknown as T : null;
    } else {
      return isPageParam
        ? {
          page: pathArray[pathArray.length - 1],
          pageId: pathArray[pathArray.length - 2],
        } as unknown as T : {
          page: undefined,
          pageId: pathArray[pathArray.length - 1],
        } as unknown as T;
    }
  },
  useGoBack() {
    const { goBack } = useHistory();
    return goBack;
  },
};

export const routerHooks: RouterHooks =
  /* istanbul ignore next */
  serverUtils.isServerSide() ? nextRouterHooks : reactRouterHooks;
